import { Box, Button, Stack } from "@mui/material"
import type { GridColDef } from "@mui/x-data-grid-pro"
import { Link } from "react-router-dom"
import { DataGrid } from "@core/components/dataGrid/dataGrid"
import { RefreshTableButton } from "../common/refreshTableButton"
import { usePostBotListState } from "../../hooks/usePostBotListState"
import { useGetTemplateVersionsQuery } from "../../services/publications/templatesApi"
import type { TemplateVersion } from "../../interface/publications"
import {
  readTemplateReturnContext,
  templateVersionPath,
  type TemplateReturnContext,
} from "./templateNavigation"
import {
  templateDateLabel,
  TemplateQueryFeedback,
  useTemplateRowCount,
} from "./templateQueryFeedback"

export function TemplateVersionsTable({
  templateId,
  listKey = "versions",
  returnContext,
}: {
  templateId: string
  listKey?: string
  returnContext?: TemplateReturnContext
}) {
  const list = usePostBotListState({ resource: "versions", key: listKey })
  const query = useGetTemplateVersionsQuery(
    { templateId, ...list.listArgs },
    { refetchOnMountOrArgChange: true },
  )
  // A failed version POST may have committed without invalidating this cache.
  const page = !query.isFetching && !query.error ? query.currentData : undefined
  const rowCount = useTemplateRowCount(templateId, page?.total)
  const columns = (
    [
      {
        field: "number",
        headerName: "Версия",
        minWidth: 150,
        flex: 1,
        renderCell: ({ row }) => (
          <Button
            component={Link}
            to={templateVersionPath(row.id)}
            state={readTemplateReturnContext(returnContext)}
          >
            Версия {row.number}
          </Button>
        ),
      },
      {
        field: "media",
        headerName: "Медиа",
        width: 100,
        valueGetter: ({ row }) => row.content.mediaIds.length,
      },
      {
        field: "createdAt",
        headerName: "Создана (МСК)",
        width: 180,
        valueFormatter: ({ value }) => templateDateLabel(value),
      },
    ] satisfies GridColDef<TemplateVersion>[]
  ).map((column) => ({ ...column, sortable: false, filterable: false }))
  return (
    <Stack spacing={2}>
      <TemplateQueryFeedback
        query={query}
        empty={page?.data.length === 0 && "Версий пока нет"}
      />
      <RefreshTableButton
        label="Обновить список версий"
        disabled={query.isFetching}
        onClick={() => void query.refetch()}
      />
      <Box sx={{ width: "100%", minHeight: 200 }}>
        <DataGrid
          {...list.gridProps}
          rows={page?.data ?? []}
          columns={columns}
          rowCount={rowCount}
          loading={query.isFetching}
          autoHeight
          disableRowSelectionOnClick
        />
      </Box>
    </Stack>
  )
}
